/**
 * The admin shell's last open screen, remembered per account.
 *
 * `active_component` is the screen of the CURRENT session: sign-out removes it,
 * and an expired or force-ended session wipes all of localStorage. So on every
 * sign-in the shell fell back to its default (Institutes for a Super Admin),
 * whatever the person had open before.
 *
 * This key outlives sign-out and is keyed by email, so a second account on the
 * same browser never inherits someone else's screen. It holds only a screen id,
 * nothing about the session. Whether that screen may still be opened is decided
 * at restore time (SuperAdminLandingPage), never here.
 */

const PREFIX = "last_active_component:";

const keyFor = (email?: string | null): string | null => {
  const e = (email || "").trim().toLowerCase();
  return e ? PREFIX + e : null;
};

export const readLastScreen = (email?: string | null): string | null => {
  const key = keyFor(email);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

export const writeLastScreen = (email: string | null | undefined, screen: string): void => {
  const key = keyFor(email);
  if (!key || !screen) return;
  try {
    localStorage.setItem(key, screen);
  } catch {
    /* storage unavailable: the shell just opens on its default next time */
  }
};

/**
 * `localStorage.clear()` that keeps the remembered screens. For the paths that
 * end a session by wiping everything (expired token, forced logout).
 */
export const clearStorageKeepingLastScreens = (): void => {
  try {
    const keep: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keep.push([k, localStorage.getItem(k) || ""]);
    }
    localStorage.clear();
    keep.forEach(([k, v]) => v && localStorage.setItem(k, v));
  } catch {
    try { localStorage.clear(); } catch { /* nothing more to do */ }
  }
};
